import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "@mhj/shared/api";
import { Card, inputClass } from "../lib/ui";

type Row = {
  applicationNo: string;
  name: string;
  ward: string;
  track: string | null;
  awardType: string | null;
};

type Results = {
  cycle: { name: string; code: string; year: number };
  publishedAt: string | null;
  count: number;
  results: Row[];
};

/**
 * The published list of successful applicants.
 *
 * Carries five fields and nothing else — no phone numbers, no NINs, no bank
 * details. Replaces the scholarship's manually uploaded spreadsheet, so it
 * cannot drift from what the database actually says.
 */
export default function Results() {
  const [query, setQuery] = useState("");
  const [ward, setWard] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["empowerment", "results"],
    queryFn: () => apiRequest<Results>("/empowerment/results"),
    retry: false,
  });

  const wards = useMemo(
    () => [...new Set((data?.results ?? []).map((r) => r.ward))].sort(),
    [data],
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (data?.results ?? []).filter(
      (r) =>
        (!ward || r.ward === ward) &&
        (!term ||
          r.name.toLowerCase().includes(term) ||
          r.applicationNo.toLowerCase().includes(term)),
    );
  }, [data, query, ward]);

  if (isLoading) return <Card><p className="text-slate-500">Loading…</p></Card>;

  if (error) {
    return (
      <Card title="No results yet">
        <p className="text-slate-600">
          Results for the current round have not been published. Everyone who
          applied is told their outcome by text message, so you do not need to
          watch this page.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card
        title={`${data!.cycle.name} — successful applicants`}
        description={`${data!.count} people. Published ${
          data!.publishedAt ? new Date(data!.publishedAt).toLocaleDateString() : ""
        }.`}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            className={inputClass}
            placeholder="Search by name or application ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className={inputClass} value={ward} onChange={(e) => setWard(e.target.value)}>
            <option value="">All wards</option>
            {wards.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </Card>

      <Card>
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-600">
            Nothing matches that. Check the spelling, or the application ID —
            it looks like JIE/{data!.cycle.year}/000123.
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((row) => (
              <div key={row.applicationNo} className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="font-medium text-slate-900">{row.name}</p>
                <p className="text-xs text-slate-500">
                  {row.applicationNo} · {row.ward} · {row.track}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
